import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ProductCard from '@/components/ProductCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/products?featured=true`),
          axios.get(`${API}/categories`)
        ]);
        setFeaturedProducts(productsRes.data);
        setCategories(categoriesRes.data.categories);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const categoryImages = {
    bikinis: 'https://customer-assets.emergentagent.com/job_intimate-apparel-19/artifacts/2l8akm82_IMG-20260216-WA0090.jpg',
    bras: 'https://customer-assets.emergentagent.com/job_intimate-apparel-19/artifacts/7bebzihb_IMG-20260216-WA0107%281%29.jpg',
    panties: 'https://customer-assets.emergentagent.com/job_intimate-apparel-19/artifacts/y32iwu20_IMG-20260216-WA0101.jpg',
    sets: 'https://customer-assets.emergentagent.com/job_intimate-apparel-19/artifacts/7bebzihb_IMG-20260216-WA0107%281%29.jpg',
    bodysuits: 'https://customer-assets.emergentagent.com/job_intimate-apparel-19/artifacts/dwpeuy2a_IMG-20260216-WA0079.jpg',
    sleepwear: 'https://customer-assets.emergentagent.com/job_intimate-apparel-19/artifacts/rvowz2ui_IMG-20260216-WA0106%281%29.jpg',
    robes: 'https://customer-assets.emergentagent.com/job_intimate-apparel-19/artifacts/fwzb9rvf_IMG-20260216-WA0113%281%29.jpg'
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1616018615050-5ad5b1f6c8b3?w=1920)',
            filter: 'brightness(0.4)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/50 to-background" />
        
        <div className="relative z-10 text-center max-w-4xl mx-auto px-4 animate-fade-in-up">
          {/* Logo */}
          <img 
            src="https://customer-assets.emergentagent.com/job_indyelegance/artifacts/w6jffw0x_IMG-20260221-WA0002.jpg" 
            alt="Indy Elegance" 
            className="w-40 h-40 mx-auto mb-8 object-contain drop-shadow-2xl"
          />
          <p className="text-accent-italic text-primary text-lg mb-4">Nova Coleção 2024</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl mb-6 leading-tight">
            Descubra a Arte da
            <span className="block gold-gradient">Sedução</span>
          </h1>
          <p className="text-muted-foreground text-lg mb-10 max-w-2xl mx-auto">
            Lingerie sofisticada que celebra a elegância feminina. 
            Cada peça é uma expressão de luxo e conforto.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/shop">
              <Button className="btn-primary" data-testid="shop-now-btn">
                Explorar Coleção
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/shop/sets">
              <Button variant="outline" className="btn-outline" data-testid="view-sets-btn">
                Ver Conjuntos
              </Button>
            </Link>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-primary rounded-full" />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 border-b border-white/5">
        <div className="container-luxury">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="card-feature text-center">
              <div className="w-12 h-12 mx-auto mb-4 border border-primary flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <h3 className="font-serif text-lg mb-2">Qualidade Premium</h3>
              <p className="text-sm text-muted-foreground">
                Materiais selecionados de alta qualidade para máximo conforto
              </p>
            </div>
            <div className="card-feature text-center">
              <div className="w-12 h-12 mx-auto mb-4 border border-primary flex items-center justify-center">
                <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <h3 className="font-serif text-lg mb-2">Envio Discreto</h3>
              <p className="text-sm text-muted-foreground">
                Embalagem elegante e discreta para sua privacidade
              </p>
            </div>
            <div className="card-feature text-center">
              <div className="w-12 h-12 mx-auto mb-4 border border-primary flex items-center justify-center">
                <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="font-serif text-lg mb-2">Compra Segura</h3>
              <p className="text-sm text-muted-foreground">
                Pagamento seguro via Zelle ou dinheiro na entrega
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Promotional Banner */}
      <section className="py-16 bg-gradient-to-r from-primary/20 via-primary/10 to-primary/20">
        <div className="container-luxury text-center">
          <p className="text-accent-italic text-primary mb-2">Oferta Especial</p>
          <h2 className="font-serif text-3xl md:text-4xl mb-4">
            Use o cupom <span className="text-primary">BEMVINDA10</span>
          </h2>
          <p className="text-muted-foreground mb-6">
            e ganhe 10% de desconto na sua primeira compra!
          </p>
          <Link to="/shop">
            <Button className="btn-primary">
              Aproveitar Agora
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20">
        <div className="container-luxury">
          <div className="text-center mb-16">
            <p className="text-accent-italic text-primary mb-2">Explore</p>
            <h2 className="font-serif text-4xl">Categorias</h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.slice(0, 6).map((category, idx) => (
              <Link
                key={category.id}
                to={`/shop/${category.id}`}
                className={`category-card relative overflow-hidden ${idx === 0 ? 'col-span-2 row-span-2 aspect-square lg:aspect-auto' : 'aspect-[3/4]'}`}
                data-testid={`category-${category.id}`}
              >
                <img
                  src={categoryImages[category.id]}
                  alt={category.name}
                  className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                />
                <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
                  <h3 className="font-serif text-2xl text-white">{category.name}</h3>
                  <p className="text-white/70 text-sm mt-1">{category.name_en}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 bg-card/30">
        <div className="container-luxury">
          <div className="flex flex-col md:flex-row justify-between items-center mb-16 gap-4">
            <div>
              <p className="text-accent-italic text-primary mb-2">Seleção Especial</p>
              <h2 className="font-serif text-4xl">Em Destaque</h2>
            </div>
            <Link to="/shop">
              <Button variant="ghost" className="btn-ghost" data-testid="view-all-btn">
                Ver Todos
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {[...Array(4)].map((_, idx) => (
                <div key={idx} className="space-y-4">
                  <div className="aspect-[3/4] skeleton" />
                  <div className="h-4 w-3/4 skeleton" />
                  <div className="h-4 w-1/4 skeleton" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1617331140180-e8262094f044?w=1920)',
          }}
        />
        <div className="container-luxury relative z-10 text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_indyelegance/artifacts/w6jffw0x_IMG-20260221-WA0002.jpg" 
            alt="Indy Elegance" 
            className="w-24 h-24 mx-auto mb-8 object-contain opacity-80"
          />
          <p className="text-accent-italic text-primary text-lg mb-4">Experiência Única</p>
          <h2 className="font-serif text-4xl md:text-5xl mb-6 max-w-3xl mx-auto leading-tight">
            Eleve seu guarda-roupa com peças que 
            <span className="gold-gradient"> fazem você se sentir especial</span>
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto mb-10">
            Cada peça é cuidadosamente selecionada para proporcionar conforto, 
            elegância e confiança em cada momento.
          </p>
          <Link to="/shop">
            <Button className="btn-primary" data-testid="explore-collection-btn">
              Descobrir Agora
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
